import apiClient from './apiClient';

/**
 * Permission Service
 * Enterprise-grade permission and role management API client
 *
 * @module permissionService
 */

const permissionService = {
  /**
   * Get all system permissions
   * Returns all 39 permissions grouped by category for employee management UI
   *
   * @returns {Promise<Object>} Object containing:
   *   - success: boolean
   *   - permissions: Array of all permissions
   *   - groupedPermissions: Object with permissions grouped by category
   *   - totalCount: Total number of permissions
   *   - pciSensitiveCount: Number of PCI-sensitive permissions
   * @throws {Error} If API request fails
   */
  getSystemPermissions: async () => {
    try {
      const response = await apiClient.get('/permissions');
      return response.data;
    } catch (error) {
      console.error('permissionService.getSystemPermissions failed:', error);
      throw error;
    }
  },

  /**
   * Get available company roles
   * Returns all roles for the current company (Admin, Manager, Employee)
   * Used to populate role selection dropdown in employee management
   *
   * @returns {Promise<Object>} Object containing:
   *   - success: boolean
   *   - roles: Array of role objects with id, name, roleType, description, permissionCount
   * @throws {Error} If API request fails
   */
  getCompanyRoles: async () => {
    try {
      const response = await apiClient.get('/permissions/roles');
      return response.data;
    } catch (error) {
      console.error('permissionService.getCompanyRoles failed:', error);
      throw error;
    }
  },

  /**
   * Get employee's complete permission data
   * Returns employee's roles, effective permissions, and PCI access status
   *
   * @param {string} employeeId - Employee UUID
   * @returns {Promise<Object>} Object containing:
   *   - success: boolean
   *   - employee: Full employee data
   *   - roles: Array of assigned roles with metadata
   *   - permissions: Array of effective permissions (union of all roles)
   *   - hasPciAccess: Boolean PCI access flag
   *   - isOwner: Boolean owner status (for UI protection)
   * @throws {Error} If employeeId is missing or API request fails
   */
  getEmployeePermissions: async (employeeId) => {
    if (!employeeId) {
      throw new Error('employeeId is required');
    }

    try {
      const response = await apiClient.get(`/employees/${employeeId}/permissions`);
      return response.data;
    } catch (error) {
      console.error('permissionService.getEmployeePermissions failed:', error);
      throw error;
    }
  },

  /**
   * Update employee permissions
   * Currently supports predefined role assignment only (Admin, Manager, Employee)
   * Custom individual permissions will be supported in future phase
   *
   * @param {string} employeeId - Employee UUID
   * @param {Object} permissionData - Permission update data
   * @param {string} [permissionData.roleId] - UUID of predefined role to assign
   * @param {string[]} [permissionData.customPermissions] - Array of permission codes (not implemented)
   * @returns {Promise<Object>} Object containing:
   *   - success: boolean
   *   - message: Success message
   *   - employee: Updated employee data
   *   - roles: Updated assigned roles
   *   - permissions: Updated effective permissions
   *   - hasPciAccess: Updated PCI access flag
   * @throws {Error} If parameters are invalid or API request fails
   */
  updateEmployeePermissions: async (employeeId, permissionData) => {
    if (!employeeId) {
      throw new Error('employeeId is required');
    }

    if (!permissionData || (!permissionData.roleId && !permissionData.customPermissions)) {
      throw new Error('permissionData must contain roleId or customPermissions');
    }

    try {
      const response = await apiClient.put(`/employees/${employeeId}/permissions`, permissionData);
      return response.data;
    } catch (error) {
      console.error('permissionService.updateEmployeePermissions failed:', error);
      throw error;
    }
  }

  // LEGACY METHODS BELOW - These endpoints are not yet implemented on backend
  // Keeping for backward compatibility, but they will throw 404 errors
  // TODO: Remove these once we confirm they're not used elsewhere in the codebase

  // getUserPermissions: async () => {
  //   const response = await apiClient.get('/users/permissions');
  //   return response.data;
  // },

  // checkPermission: async (permissionCode) => {
  //   const response = await apiClient.post('/users/check-permission', {
  //     permission: permissionCode
  //   });
  //   return response.data;
  // },

  // checkPermissions: async (permissionCodes) => {
  //   const response = await apiClient.post('/users/check-permissions', {
  //     permissions: permissionCodes
  //   });
  //   return response.data;
  // },

  // getCompanyRoles: async (companyId) => {
  //   const response = await apiClient.get(`/companies/${companyId}/roles`);
  //   return response.data;
  // },

  // getRolePermissions: async (roleId) => {
  //   const response = await apiClient.get(`/roles/${roleId}/permissions`);
  //   return response.data;
  // },

  // updateRolePermissions: async (roleId, permissions) => {
  //   const response = await apiClient.put(`/roles/${roleId}/permissions`, {
  //     permissions
  //   });
  //   return response.data;
  // },

  // assignEmployeeRole: async (employeeId, roleId) => {
  //   const response = await apiClient.post(`/employees/${employeeId}/roles`, {
  //     roleId
  //   });
  //   return response.data;
  // },

  // removeEmployeeRole: async (employeeId, roleId) => {
  //   const response = await apiClient.delete(`/employees/${employeeId}/roles/${roleId}`);
  //   return response.data;
  // }
};

export default permissionService;
